import React, { Suspense } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Redirect, Route, Switch } from "react-router-dom";
import ErrorBoundary from "../Components/ErrorBoundary/ErrorBoundary";
import ProtectedRoute from "./ProtectedRoute";
import { getOriginUrlAndOrgId } from "../Utils/utils";
import { opensOnConversation } from "../Store/Selectors/opensOnConversation";

// Lazy-load pages for route-level code splitting
const AccountCreated = React.lazy(() => import("./AccountCreated/AccountCreated"));
const ChatServices = React.lazy(() => import("./ChatServices/ChatServices"));
const Event = React.lazy(() => import("./Event/Event"));
const ChatHuman = React.lazy(() => import("./ChatHuman/ChatHuman"));
const ForgetPassword = React.lazy(() => import("./ForgetPassword/ForgetPassword"));
const HomeWithoutMessage = React.lazy(() => import("./HomeWithoutMessage/HomeWithoutMessage"));
const Login = React.lazy(() => import("./Login/Login"));
const Metaverse = React.lazy(() => import("./Metaverse/Metaverse"));
const NewPassword = React.lazy(() => import("./NewPassword/NewPassword"));
const OtpPage = React.lazy(() => import("./OtpPage/OtpPage"));
const ProductDetails = React.lazy(() => import("./ProductDetails/ProductDetails"));
const Products = React.lazy(() => import("./Products/Products"));
const SignUp = React.lazy(() => import("./SignUp/SignUp"));
const VideoChat = React.lazy(() => import("./VideoChat/VideoChat"));
const VirtualTour = React.lazy(() => import("./VirtualTour/VirtualTour"));
const GroupChannels = React.lazy(() => import("./GroupChannels/GroupChannels"));
const Profile = React.lazy(() => import("./Profile/Profile"));
const UserDetails = React.lazy(() => import("./UserDetails/UserDetails"));
const Admin = React.lazy(() => import("./Admin/Admin"));
const ReferralProgram = React.lazy(() => import("./ReferralProgram/ReferralProgram"));
const ReferralInviteFriend = React.lazy(() => import("./ReferralProgram/ReferralInviteFriend"));
const ReferralOtp = React.lazy(() => import("./ReferralProgram/ReferralOtp"));
const ReferralHistoryList = React.lazy(() => import("./ReferralProgram/ReferralHistoryList"));
const FriendUsedReferrals = React.lazy(() => import("./ReferralProgram/FriendUsedReferrals"));
const EventHosts = React.lazy(() => import("./EventHosts/EventHosts"));
const Wallet = React.lazy(() => import("../Components/Wallet/Wallet"));
const WalletPass = React.lazy(() => import("../Components/Wallet/WalletPass"));
const PointsHistoryPage = React.lazy(() => import("../Components/Wallet/PointsHistoryPage"));
const TaskDetails = React.lazy(() => import("./TaskDetails/TaskDetails"));
const Menu = React.lazy(() => import("./Menu/Menu"));
const Tasks = React.lazy(() => import("./Tasks/Tasks"));

const MainPage = ({ chatSettings, props, org }) => {
  // On a shop the conversation, unless the visitor asked for the front door.
  // The reasoning lives in the selector.
  if (opensOnConversation({
    leanChrome: getOriginUrlAndOrgId().leanChrome,
    // Carried by the conversation's back control, which asks for this screen
    // by name. Without it the rule below would hand the visitor straight back
    // to the conversation they just left.
    frontDoorAsked: Boolean(props?.location?.state?.frontDoor),
  })) {
    return <ErrorBoundary><ChatHuman {...props} org={org} /></ErrorBoundary>;
  }
  // Always show the original behavior regardless of login status
  if (chatSettings?.withMessage) {
    return <ChatServices {...props} />;
  } else {
    return <HomeWithoutMessage {...props} />;
  }
};

const AppRouter = ({ org, chatSettings, shops, preview }) => {
  const { isLoggedIn } = useSelector((state) => state.auth);
  return (
    <Suspense fallback={null}>
    <Switch>
      <Route
        exact
        path="/"
        render={(props) => {
          return (
            <MainPage chatSettings={chatSettings} props={props} org={org} />
          );
        }}
      />
      <Route
        path="/chat"
        render={(props) => (
          <ErrorBoundary>
            {getOriginUrlAndOrgId().leanChrome
              ? <ChatHuman {...props} org={org} />
              : <ChatServices {...props} org={org} />}
          </ErrorBoundary>
        )}
      />
      <Route
        path="/chat-human"
        render={(props) => <ErrorBoundary><ChatHuman {...props} org={org} /></ErrorBoundary>}
      />
      <Route
        exact
        path="/metaverse"
        render={(props) => (
          <ErrorBoundary>
            <Metaverse
              {...props}
              isLoggedIn={isLoggedIn}
              orgId={org?.id}
              org={org}
              preview={preview}
            />
          </ErrorBoundary>
        )}
      />
      <Route
        exact
        path="/products"
        render={(props) => (
          <Products
            {...props}
            orgId={org?.id}
            org={org}
            currency={org?.currency}
          />
        )}
      />
      <Route
        exact
        path="/virtual-tour"
        render={(props) => (
          <ErrorBoundary>
            <VirtualTour
              {...props}
              shops={shops}
              org={org}
              defaultImage={
                chatSettings?.images?.chat_services_widget_background ||
                chatSettings?.images?.logo_url
              }
            />
          </ErrorBoundary>
        )}
      />
      <Route
        exact
        path="/group-channels"
        render={(props) => (
          <GroupChannels {...props} org={org} preview={preview} />
        )}
      />
      <ProtectedRoute
        path="/profile"
        render={(props) => <Profile {...props} />}
      />
      <ProtectedRoute
        path="/user-details"
        render={(props) => <UserDetails {...props} />}
      />
      <Route
        path="/products/:id"
        render={(props) => (
          <ProductDetails
            {...props}
            orgDomain={org?.domain}
            org={org}
            currency={org?.currency}
          />
        )}
      />
      <ProtectedRoute
        path="/metaverse/:id/hosts"
        render={(props) => <ErrorBoundary><EventHosts {...props} /></ErrorBoundary>}
      />
      <ProtectedRoute
        path="/metaverse/:id"
        render={(props) => (
          <ErrorBoundary>
            <Event {...props} isLoggedIn={isLoggedIn} preview={preview} />
          </ErrorBoundary>
        )}
      />
      <Route path="/login" render={(props) => <Login {...props} org={org} />} />
      <Route
        path="/signup"
        render={(props) => <SignUp {...props} org={org} />}
      />
      <Route
        path="/account-created"
        render={(props) => <AccountCreated {...props} org={org} />}
      />
      <Route
        path="/new-password"
        render={(props) => <NewPassword {...props} org={org} />}
      />
      <Route path="/otp" render={(props) => <OtpPage {...props} org={org} />} />
      <Route
        path="/forget-password"
        render={(props) => <ForgetPassword {...props} org={org} />}
      />
      <ProtectedRoute
        path="/video-chat"
        render={(props) => <ErrorBoundary><VideoChat {...props} orgId={org?.id} org={org} /></ErrorBoundary>}
      />
      <ProtectedRoute path="/admin" render={(props) => <ErrorBoundary><Admin {...props} /></ErrorBoundary>} />
      <Route path="/referral-program" component={ReferralProgram} />
      <Route
        path="/referral-invite-friend"
        render={(props) => <ReferralInviteFriend {...props} org={org} />}
      />
      <Route path="/referral-otp" component={ReferralOtp} />
      <Route
        path="/referral-history-list"
        render={(props) => <ReferralHistoryList {...props} />}
      />
      <Route
        path="/friend-used-referrals"
        render={(props) => <FriendUsedReferrals {...props} />}
      />
      <Route
        path="/wallet"
        render={(props) => <ErrorBoundary><Wallet {...props} /></ErrorBoundary>}
      />
      <ProtectedRoute
        path="/wallet-pass"
        render={(props) => <ErrorBoundary><WalletPass {...props} /></ErrorBoundary>}
      />
      <ProtectedRoute
        path="/points-history"
        render={(props) => <ErrorBoundary><PointsHistoryPage {...props} /></ErrorBoundary>}
      />
      <ProtectedRoute
        exact
        path="/quest/:id"
        render={(props) => <TaskDetails {...props} />}
      />

      <Route path="/menu" render={(props) => <Menu {...props} />} />
      <Route path="/tasks" render={(props) => <Tasks {...props} />} />

      <Route path="*">
        <Redirect to="/" />
      </Route>
    </Switch>
    </Suspense>
  );
};

AppRouter.propTypes = {
  org: PropTypes.object,
  chatSettings: PropTypes.object,
  shops: PropTypes.array,
  preview: PropTypes.bool,
};
export default AppRouter;
